import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../api/client';
import JobProgress from './JobProgress';
import useJobs from '../hooks/useJobs';
import { useToast } from './Toasts';
import { TYPE_LABEL } from '../constants/jobs';

function statusIcon(s) {
  if (s === 'running') return <span className="spinner tiny" />;
  if (s === 'done') return <span className="ti ok">✓</span>;
  return <span className="ti ko">✕</span>;
}

/** Les tâches achevées disparaissent du panneau au bout de 2 h (historique complet sur /taches). */
const MAX_AGE_HOURS = 2;
/** Autant de lignes que le panneau en montrait quand il interrogeait le serveur lui-même. */
const MAX_ITEMS = 15;

/**
 * Panneau flottant listant les tâches en arrière-plan (en cours + récentes).
 * Présent sur toutes les pages internes ; il lit la liste partagée par `useJobs`.
 */
export default function TasksPanel() {
  const { jobs: all, refresh } = useJobs();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const prevRunning = useRef(0);

  // Même filtre que `maxAgeHours` côté serveur : une tâche en cours reste
  // toujours visible, une tâche achevée s'efface au bout de deux heures.
  const jobs = useMemo(() => {
    const since = Date.now() - MAX_AGE_HOURS * 3600 * 1000;
    return all
      .filter((j) => j.status === 'running' || new Date(j.updatedAt || j.createdAt).getTime() >= since)
      .slice(0, MAX_ITEMS);
  }, [all]);

  const running = jobs.filter((j) => j.status === 'running');

  // Ouvre automatiquement le panneau quand une tâche démarre.
  useEffect(() => {
    if (running.length > prevRunning.current) setOpen(true);
    prevRunning.current = running.length;
  }, [running.length]);

  const goto = (job) => {
    const clientId = job.client && (job.client._id || job.client);
    if (clientId) navigate(`/clients/${clientId}`);
  };
  const cancel = async (jobId) => {
    try {
      await api.cancelJob(jobId);
      refresh();
    } catch (e) {
      toast(`Annulation impossible : ${e.message}`, 'error');
    }
  };

  if (jobs.length === 0) return null;

  return (
    <div className={`tasks-panel ${open ? 'open' : ''}`}>
      <button
        type="button"
        className="tasks-head"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls="tasks-list"
      >
        <span>Tâches</span>
        {running.length > 0
          ? <span className="tasks-badge running"><span className="spinner tiny" /> {running.length}</span>
          : <span className="tasks-badge">{open ? '▾' : '▴'}</span>}
      </button>

      {open && (
        <div className="tasks-list" id="tasks-list">
          {jobs.slice(0, 8).map((j) => (
            <div key={j._id} className={`task-item ${j.status}`}>
              {/* La ligne est un vrai bouton : atteignable au clavier, annoncé
                  comme tel ; l'annulation est son frère, pas son enfant. */}
              <button
                type="button"
                className="task-open"
                onClick={() => goto(j)}
                title={j.client ? 'Ouvrir la fiche client' : undefined}
              >
                {statusIcon(j.status)}
                <span className="task-main">
                  <span className="task-label">
                    {TYPE_LABEL[j.type] || j.type}
                    {j.status === 'running' && j.progress > 0 && (
                      <span className="task-pct">{Math.round(j.progress)} %</span>
                    )}
                  </span>
                  <span className="task-sub">
                    {j.status === 'running' && j.step ? j.step : (j.client?.name || j.label)}
                    {j.status === 'error' && j.error ? ` — ${j.error.slice(0, 60)}` : ''}
                  </span>
                  {j.status === 'running' && <JobProgress value={j.progress} step={j.step} compact />}
                </span>
              </button>
              {j.status === 'running' && (
                <button
                  type="button"
                  className="task-cancel"
                  title="Annuler"
                  aria-label={`Annuler : ${TYPE_LABEL[j.type] || j.type}`}
                  onClick={() => cancel(j._id)}
                >
                  Annuler
                </button>
              )}
            </div>
          ))}
          <Link to="/taches" className="tasks-all">Voir toutes les tâches →</Link>
        </div>
      )}
    </div>
  );
}
